/**
 * HelpDrawer — slide-out panel listing route-linked support articles / FAQs.
 */

import { useEffect, useId, useRef, type RefObject } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import XButton from '@/components/shared/custom-buttons/X'
import type { EntityType, KnowledgeEntity } from '@/types/knowledge'

interface HelpDrawerProps {
  open: boolean
  onClose: () => void
  articles: KnowledgeEntity[]
  /** Element to restore focus to when the drawer closes. */
  returnFocusRef?: RefObject<HTMLElement | null>
}

function typeLabel(type: EntityType): string {
  switch (type) {
    case 'faq-entry':
      return 'FAQ'
    case 'support-article':
      return 'Article'
    case 'help-article':
      return 'Help'
    default:
      return 'Help'
  }
}

function entityTitle(entity: KnowledgeEntity): string {
  const meta = entity.metadata ?? {}
  if (typeof meta.question === 'string' && meta.question.trim()) return meta.question
  if (typeof meta.title === 'string' && meta.title.trim()) return meta.title
  return entity.name
}

function entityBody(entity: KnowledgeEntity): string {
  const meta = entity.metadata ?? {}
  const parts: string[] = []
  for (const key of ['answer', 'description', 'summary', 'content', 'body'] as const) {
    const value = meta[key]
    if (typeof value === 'string' && value.trim()) parts.push(value.trim())
  }
  return parts.join('\n\n')
}

export function HelpDrawer({ open, onClose, articles, returnFocusRef }: HelpDrawerProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    // Move focus into the dialog for screen-reader / keyboard users
    panelRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const restoreTarget = returnFocusRef?.current ?? previouslyFocused
      restoreTarget?.focus?.()
    }
  }, [open, onClose, returnFocusRef])

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={onClose} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-background shadow-2xl outline-none"
        data-testid="route-help-drawer"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-base font-semibold">
              Help for this page
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Guides and answers related to what you&apos;re viewing.
            </p>
          </div>
          <XButton onClick={onClose} aria-label="Close help" />
        </header>

        <ScrollArea className="min-h-0 flex-1">
          <div className="px-4 py-3">
            <Accordion
              type="multiple"
              className="w-full"
              defaultValue={articles.slice(0, 1).map((a) => a.id)}
            >
              {articles.map((article) => {
                const title = entityTitle(article)
                const body = entityBody(article)
                return (
                  <AccordionItem key={article.id} value={article.id}>
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5 pr-2">
                        <Badge variant="secondary" className="rounded-md">
                          {typeLabel(article.type)}
                        </Badge>
                        <span className="text-sm font-medium leading-snug">{title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      {body ? (
                        <div className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert">
                          <ReactMarkdown>{body}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No additional details are available for this entry.
                        </p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )
              })}
            </Accordion>
          </div>
        </ScrollArea>

        <footer className="shrink-0 border-t px-4 py-3">
          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </footer>
      </aside>
    </>
  )
}
